/**
 * Cross-domain enums + pgRole declarations for the lodeX Pro Postgres schema.
 *
 * Phase 2 scaffold (Wave 1 / Plan 02-01): pinned consts + pgEnum +
 * pgRole declarations imported by every other schema file in this directory.
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - JOB_STATUS_CODES — exactly 16 values derived from master spec §8
 *     (13 chain + 1 modifier + 2 terminal). The sort_order column in
 *     job_statuses (Plan 02-02) derives from this array's index. Changing
 *     order = re-seeding. The boot-time assertion (Plan 02-07) reads
 *     JOB_STATUS_CODES.length and refuses to start if the seeded row
 *     count diverges.
 *   - TENANT_USER_ROLES enum values — text + CHECK constraint at the
 *     tenant_users.role column (Plan 02-01); adding a value = ALTER CHECK
 *     constraint, removing a value = data migration.
 *   - appUserRole / migratorRole names — match the CREATE ROLE statements
 *     hand-edited into Plan 02-05's 0000_init.sql.
 *
 * Why pgEnum for tenant_type but text+CHECK for role: tenant_type is the 4
 * tenant types from master spec §5 (locked at 4 forever — adding a 5th
 * would be a multi-phase architectural change). Roles will gain new
 * categories over time (e.g. compliance officer); pgEnum ALTER is fragile
 * (RESEARCH Open Question #1 + drizzle issue #4295), text+CHECK is not.
 *
 * Sources:
 *   - master spec §5 (TENANT TYPES — Hauler/Operator/Disposal/Platform)
 *   - master spec §6 (10-role permission matrix)
 *   - master spec §8 (16-state DEFINITIVE model: 13 chain + 1 modifier + 2 terminal)
 *   - master spec §35 (LEAD_DRIVER + ACTIVE/SUSPENDED/OFFBOARDED status)
 *   - CONTEXT D-08 (JOB_STATUS_CODES pinned const + boot-time assertion)
 *   - CONTEXT D-15 (two-role split: app_user NOINHERIT, migrator BYPASSRLS)
 *   - CONTEXT D-16 (platform-catalog tables get NO RLS — appUserRole reads,
 *     migratorRole mutates; enforced by GRANT/REVOKE in Plan 02-05's epilogue)
 *   - DEC-015 (tenant_users consolidates driver_tenant_memberships)
 *
 * Why this shape now: DB-02, DB-03 require this; DB-08 requires the role
 * declarations for pgPolicy `to:` references in Plan 02-02/02-03/02-04.
 */

import { pgEnum, pgRole } from 'drizzle-orm/pg-core';
import { TENANT_TYPES } from './enum-values.js';

// The pure value tuples live in a drizzle-free module so a BROWSER can import them
// without pulling drizzle-orm in behind them (that is how ~22 drizzle references reached
// the web-dispatch bundle). Re-exported here, so all 36 importers of this file — and the
// API — are unaffected and no call site changed. The contracts themselves are unchanged
// and still locked; see enum-values.ts.
export * from './enum-values.js';

export const tenantTypeEnum = pgEnum('tenant_type', TENANT_TYPES);

/**
 * App-user role — runtime API connects as this. RLS enforced. Phase 2's
 * migration (Plan 02-05) hand-edits the generated CREATE ROLE statement
 * to append NOINHERIT and the GRANT/REVOKE block (CONTEXT D-15, D-16).
 *
 * Imported by every pgPolicy `to:` clause in users.ts / jobs.ts / drivers.ts /
 * sites.ts / hauls.ts / disposals.ts / audit.ts / billing.ts / yards.ts /
 * trailers.ts. Re-declaring `pgRole('app_user')` in another schema file would
 * cause drizzle-kit to emit duplicate CREATE ROLE statements — DON'T.
 */
export const appUserRole = pgRole('app_user');

/**
 * Migrator role — drizzle-kit and seeds connect as this. BYPASSRLS for DDL
 * + cross-tenant catalog seeds. Phase 2's migration (Plan 02-05) hand-edits
 * the generated CREATE ROLE statement to append BYPASSRLS.
 *
 * Not referenced by any pgPolicy (BYPASSRLS bypasses all policies). Declared
 * here so drizzle-kit's `entities.roles: true` setting in drizzle.config.ts
 * emits a single CREATE ROLE statement at the top of 0000_init.sql.
 */
export const migratorRole = pgRole('migrator');
